import { assert, Callback1, lazy } from '@deqo/foundation';
import { toBase64 } from 'js-base64';
import { AbstractObject } from './AbstractObject';
import { ComponentChildren } from './ComponentChildren';
import { ComponentPolicy } from './ComponentPolicy';
import { ComponentTuple } from './ComponentTuple';
import { Convention } from './Convention';
import { Field } from './Field';
import * as FieldSymbols from './FieldSymbols';
import { ComponentDefinition, ComponentDescriptor, ComponentType, HasProperties } from './Interfaces';
import { Manifest } from './Manifest';
import { MemoStore } from './MemoStore';
import { memoUsing } from './MemoUsing';
import { Fields, Host, Id } from './ObjectSymbols';

const toRef = (id: string) => `$${toBase64(id).toUpperCase()}`;

export function component(def: ComponentDefinition): ClassDecorator {
    // tslint:disable-next-line: ban-types
    return (type: any): void => {
        // Fill in defaults
        def = Object.assign({ implements: [] }, def);
        // Register
        Manifest.registerComponent(type, def);
    };
}

export type ComponentPredicate<T extends Component = Component> = (x: T) => boolean;

export interface ComponentJson extends Record<string, any> {
    id: string;
    type: string;
    children: ComponentJson[];
}

export class Component extends AbstractObject implements HasProperties {

    public static describe<T extends Component>(instance: T): ComponentDescriptor<T>;

    public static describe<T extends Component>(type: ComponentType<T>): ComponentDescriptor<T>;

    public static describe<T extends Component>(desc: ComponentDescriptor<T>): ComponentDescriptor<T>;

    public static describe(typeName: string): ComponentDescriptor;

    public static describe(input: any): any {
        if (input instanceof Component) return this.describe((input as any).constructor);
        if (typeof (input) === 'object' && 'type' in input) return this.describe(input.type);
        return Manifest.describeComponent(input);
    }

    public static hasProperty<T extends Component>(instance: T, propertyName: string): ComponentDescriptor<T>;

    public static hasProperty<T extends Component>(type: ComponentType<T>, propertyName: string): ComponentDescriptor<T>;

    public static hasProperty<T extends Component>(desc: ComponentDescriptor<T>, propertyName: string): ComponentDescriptor<T>;

    public static hasProperty(typeName: string, propertyName: string): ComponentDescriptor;

    public static hasProperty(input: any, propertyName: string): any {
        const desc = Component.describe(input);
        return !!Manifest.describeProperty(desc.type, propertyName);
    }

    public static compareDepth(a: Component, b: Component) {
        while (true) {
            if (a.parent === b) return 1;
            if (b.parent === a) return -1;
            if (a.parent === b.parent) return a.order - b.order;
            const ad = a.depth();
            const bd = b.depth();
            if (ad > bd || ad === bd) a = a.parent;
            if (bd > ad || bd === ad) b = b.parent;
        }
    }

    public readonly [Fields]: Record<string, Field>;

    constructor() {
        super();
        const cd = Component.describe(this);
        const fx = cd.fields.map(fd => {
            const fi = new fd.type();
            fi[FieldSymbols.Name] = fd.name;
            fi[FieldSymbols.Owner] = this;
            return [fd.name, fi];
        });
        this[Fields] = Object.fromEntries(fx);
    }

    @lazy()
    public get fields(): readonly Field[] {
        return Object.values(this[Fields]);
    }

    @memoUsing('childrenInternal.version')
    public get children(): readonly Component[] {
        return this.childrenInternal.toArray();
    }

    public get parent(): Component {
        return this[Host].getParent(this);
    }

    public get order(): number {
        if (this.parent === null) return -1;
        return this.parent.children.indexOf(this);
    }

    @lazy()
    protected get childrenInternal(): ComponentChildren {
        const cc = new ComponentChildren();
        cc[Id] = this[Id];
        cc[Host] = this[Host];
        return cc;
    }

    protected get policy() {
        return ComponentPolicy.default;
    }

    public mounted(): boolean {
        if (!this.alive) return false;
        const p = this.parent;
        return !!p ? p.mounted() : false;
    }

    public ancestors(): Component[] {
        const arr = [] as Component[];
        let a = this.parent;
        while (a) {
            arr.push(a);
            a = a.parent;
        }
        return arr;
    }

    public ancestorTop(): Component | null {
        let a = this.parent;
        while (a?.parent) {
            a = a.parent;
        }
        return a;
    }

    public depth(): number {
        return this.parent ? this.parent.depth() + 1 : 0;
    }

    public as<T extends Component>(convention: ComponentType<T> | Convention<T>): T | null;

    public as<T extends Component>(convention: ComponentType<T> | Convention<T>, action: (t: T) => void): T | null;

    public as(typeOrConvention: any, action?: any): any | null {
        const casted = this.is(typeOrConvention) ? this : null;
        if (casted && !!action) {
            action(casted);
        }
        return casted;
    }

    public cast<T extends Component>(type: ComponentType<T>): T;

    public cast<T>(convention: Convention<T>): T;

    public cast(typeOrConvention: any): any {
        assert(this.is(typeOrConvention), `Component.cast: Type could not be cast to ${Manifest.debugString(typeOrConvention)}.`);
        return this;
    }

    public is(typeOrConvention: ComponentType | Convention<any>): boolean {
        if (typeOrConvention instanceof Convention) {
            return Manifest.doesTypeImplementConvention(this.constructor as any, typeOrConvention);
        }
        else {
            return this instanceof typeOrConvention;
        }
    }

    public isAnd<T extends Component>(typeOrConvention: ComponentType<T> | Convention<T>, predicate: (x: T) => boolean): boolean {
        return this.is(typeOrConvention) && predicate(this as any);
    }

    public field(name: string): Field {
        const f = this[Fields][name] ?? null;
        assert(!!f, `${this.type}: field ${name} does not exist.`);
        return f;
    }

    public polarize<T extends Component>(type: ComponentType<T>): T[];

    public polarize<T>(convention: Convention<T>): T[];

    public polarize(typeOrConvention: any): any[] {
        const arr = [] as any[];
        for (const c of this.children) {
            if (c.alive && c.is(typeOrConvention)) {
                arr.push(c);
            }
        }
        return arr;
    }

    public isAncestorOf(another: Component): boolean {
        return another.isDescendantOf(this);
    }

    public isDescendantOf(another: Component | ComponentPredicate): boolean {
        const predicate = another instanceof Function ? another : (x: Component) => x === another;

        if (this.parent === null) return false;
        if (predicate(this.parent)) return true;
        return this.parent.isDescendantOf(another);
    }

    public isAbove(another: Component) {
        return Component.compareDepth(this, another) > 0;
    }

    public isBehind(another: Component) {
        return Component.compareDepth(this, another) < 0;
    }

    public has(propertyName: string): any {
        return !!this.resolvePropertyFromName(propertyName);
    }

    public get(propertyName: string): any {
        const pd = this.resolvePropertyFromName(propertyName);
        assert(!!pd, `Property ${propertyName} is not defined on ${this.type}`);
        const sv = this[Host].getProperty(this, pd.name) ?? (
            !!pd.oldName
                ? this[Host].getProperty(this, pd.oldName)
                : undefined
        );

        // If type is byRef, use memoize strategy to ensure consistent returns in between mutations
        if (pd.type.byRef) {
            const key = `property:${pd.name}`;
            const store = MemoStore.get(this);
            const source = [sv];
            const lastResult = store.getIfHit(key, source);
            if (lastResult) return lastResult;
            const result = pd.type.deserialize(sv, pd.default);
            store.set(key, source, result);
            return result;
        }
        else {
            return pd.type.deserialize(sv, pd.default);
        }
    }

    public set(propertyName: string, value: any): void {
        const pd = this.resolvePropertyFromName(propertyName);
        assert(!!pd, `Property ${propertyName} is not defined on ${this.type}`);
        const [allow, err] = this.policy.allowSet(pd.name, value);
        assert(!!allow, `Setting ${pd.name} on ${this.type} violates component policy: ${err}`);
        const sv = pd.type.serialize(value);
        this[Host].setProperty(this, pd.name, sv);
    }

    public setMaybe(propertyName: string, value: any): void {
        const currentValue = this.get(propertyName);
        if (currentValue !== value) {
            this.set(propertyName, value);
        }
    }

    public nextSibling(): Component | null {
        return this.sibling(1);
    }

    public prevSibling(): Component | null {
        return this.sibling(-1);
    }

    public sibling(offset: number): Component | null {
        const p = this.parent;
        if (!p) return null;
        const i = p.children.indexOf(this);
        if (i < 0) return null;
        return p.children[i + offset] ?? null;
    }

    public relations<T extends Component>(relationshipName, type?: ComponentType<T>): readonly T[];
    public relations<T extends Component>(relationshipName, conv?: Convention<T>): readonly T[];
    public relations(relationshipName: string, typeOrConvention?: any): readonly any[] {
        const related = this[Host].getRelated(this, relationshipName);
        return (typeOrConvention)
            ? related.filter(x => x.is(typeOrConvention))
            : related;
    }

    public add(child: Component): this {
        const [allow, err] = this.policy.allowAdd(child);
        if (!allow) throw new Error(`add failure: ${err}`);
        this[Host].append(this, child);
        return this;
    }

    public addAtBack(child: Component): this {
        return this.addBefore(this.children[0] ?? null, child);
    }

    public addBefore(before: Component, child: Component): this {
        const [allow, err] = this.policy.allowAdd(child);
        if (!allow) throw new Error(`addBefore failure: ${err}`);
        if (before?.parent !== this) return this.add(child);
        this[Host].append(this, child, before);
        return this;
    }

    public addAfter(after: Component, child: Component): this {
        // TODO: Implement this properly at mutation level
        const index = this.childrenInternal.indexOf(after);
        if (index < 0) return this.add(child);
        const afterAfter = this.childrenInternal.get(index + 1);
        if (!afterAfter) return this.add(child);
        return this.addBefore(afterAfter, child);
    }

    public addToParentBeforeSelf(sibling: Component): this {
        if (!this.parent) throw new Error(`addBeforeSelf failure: ${this.id} has no parent.`);
        this.parent.addBefore(this, sibling);
        return this;
    }

    public addToParentAfterSelf(sibling: Component): this {
        if (!this.parent) throw new Error(`addBeforeSelf failure: ${this.id} has no parent.`);
        this.parent.addAfter(this, sibling);
        return this;
    }

    public remove(child: Component): this {
        const [allow, err] = this.policy.allowRemove(child);
        if (!allow) throw new Error(`remove failure: ${err}`);
        this[Host].truncate(this, child);
        return this;
    }

    public removeAll(): Component[] {
        const children = Array.from(this.children);
        for (const ch of children) {
            this.remove(ch);
        }
        return children;
    }

    public removeSelf(): this {
        if (!this.parent) throw new Error(`removeSelf failure: ${this.id} has no parent.`);
        this.parent.remove(this);
        return this;
    }

    public flatten(includeSelf?: boolean): Component[] {
        if (includeSelf) {
            return [this as Component].concat(this.flatten(false));
        }
        else {
            return this.children.flatMap(x => x.flatten(true));
        }
    }

    public visitAll(visitor: Callback1<boolean | void, Component>): void;
    public visitAll<T>(conv: Convention<T>, visitor: Callback1<boolean | void, T>): void;
    public visitAll(...args: any[]) {
        if (args.length === 1) args = [null].concat(args);
        const [conv, visitor] = args;
        for (const c of this.children) {
            if (c.alive && (!conv || c.is(conv)) && visitor(c) !== false) {
                c.visitAll(conv, visitor);
            }
        }
    }

    public toShallowTuple(): ComponentTuple {
        const state = this[Host].getState(this);
        const data = {} as any;
        for (const key of Object.keys(state)) {
            if (key === '__v') continue;
            // Support assocation property substitution
            if (key.match(/@\w+/)) {
                data[key] = toRef(state[key]);
            }
            else {
                data[key] = state[key];
            }
        }
        return [this.type, toRef(this.id), data, []];
    }

    public toTuple(): ComponentTuple {
        const [type, ref, data] = this.toShallowTuple() as any;
        return [type, ref, data, this.children.map(x => x.toTuple())];
    }

    public toString() {
        return `${this.type}[${this.id}]`;
    }

    public toHumanString() {
        return `${this.type}[${this.id}]`;
    }

    public toJson(): ComponentJson {
        const [type, _ref, data] = this.toShallowTuple() as any;
        return {
            id: this.id,
            type,
            ...data,
            children: this.children.map(x => x.toJson())
        };
    }

    public toText() {
        return this.toString();
    }

    protected resolvePropertyFromName(name: string) {
        const cd = Manifest.describeComponent(this.type);
        const pd = Manifest.describeProperty(cd.type, name);
        if (pd) {
            return pd;
        }
        // If no direct match, see if we have a field with this name
        const fd = Manifest.describeField(cd.type, name);
        // If we do, see if name|value is a property
        if (fd) {
            const fvpd = Manifest.describeProperty(cd.type, `${name}|value`);
            if (fvpd) {
                return fvpd;
            }
        }
        // Otherwise null
        return null;
    }
}
